import { JsonPipe, NgClass, NgStyle, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, ElementRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepickerInputEvent, MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { CUSTOM_FIELD_TYPES, MIME_TYPES } from '@constants/app.constants';
import { DATE_FORMAT, DATE_FORMATS } from '@constants/date.constants';
import { TEMPLATE_CONSTANT } from '@constants/template.constants';
import { PhishingProtectionDirective } from '@directives/app-phising-protection.directive';
import { environment } from '@environments/environment';
import { CustomFieldModel, CustomFieldOptionModel } from '@models/custom-field.model';
import { CustomCustomFieldFileDbModel, CustomerCustomFieldDbModel } from '@models/customer.model';
import { StoreDbModel } from '@models/store-db.model';
import { WidgetConfModel } from '@models/widget-conf.model';
import { WorkerDbModel } from '@models/worker-db.model';
import { NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CalioMeetingTemplatePipe } from '@pipes/calio-meeting-template.pipe';
import { CalioSafePipe } from '@pipes/calio-safe.pipe';
import { CalioTranslateResolverPipe } from '@pipes/calio-translate-resolver.pipe';
import { CalioTranslationPipe } from '@pipes/calio-translation.pipe';
import { IsEmptyStringPipe } from '@pipes/is-empty-string.pipe';
import { BookingService } from '@services/feature/booking.service';
import { CustomFieldsService } from '@services/feature/custom-fields.service';
import { SurveyService } from '@services/feature/survey.service';
import { WidgetService } from '@services/feature/widget.service';
import { WorkerService } from '@services/feature/worker.service';
import { DateUtilService } from '@services/utils/date-util.service';
import { HelperService } from '@services/utils/helper.service';
import { LoggerService } from '@services/utils/logger.service';
import { workerServesStore } from '@services/utils/store-scope.util';
import { SwRouteService } from '@services/utils/sw-route.service';
import { InfoCardComponent } from '@ui-lib/cards/info-card/info-card.component';
import { ImageModalComponent } from '@ui-lib/modals/image-modal/image-modal.component';
import { YoutubeModalComponent } from '@ui-lib/modals/youtube-modal/youtube-modal.component';
import { CswLinkComponent } from '@ui-lib/typography/csw-link/csw-link.component';
import { CswTextComponent } from '@ui-lib/typography/csw-text/csw-text.component';

const components = [InfoCardComponent, CswLinkComponent, CswTextComponent,];
const modules = [NgSelectModule, FormsModule, MatFormFieldModule, MatInputModule, MatDatepickerModule, MatCheckboxModule, MatIconModule, TranslateModule];
const pipes = [
  CalioSafePipe, CalioTranslationPipe, IsEmptyStringPipe,
  CalioMeetingTemplatePipe, JsonPipe, CalioTranslateResolverPipe,
];
const directives = [PhishingProtectionDirective];

@Component({
  selector: 'app-booking-question',
  templateUrl: './booking-question.component.html',
  styleUrls: ['./booking-question.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
  providers: [{
    provide: DateAdapter,
    useClass: MomentDateAdapter,
    deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
  }, {
    provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS
  }],
  imports: [NgClass, NgTemplateOutlet, NgStyle, ...modules, ...components, ...pipes, ...directives]
})
export class BookingQuestionComponent implements OnInit {

  readonly deployUrl = environment.deployUrl;
  readonly customFieldConst = CUSTOM_FIELD_TYPES;
  readonly imagePath = environment.imageHostName;
  readonly workerAvatarBaseUrl = environment.workerAvatarBaseUrl;
  readonly dateFormat = DATE_FORMAT;
  readonly templateConstant = TEMPLATE_CONSTANT;

  @Input() customerDataIsNotMandatoryToUploadFiles = true;
  @Input() isNextButtonPressed = false;
  @Input() deactivateField = false;
  @Input() prefix: string;
  @Input() customField: CustomFieldModel;
  @Input() selectedCustomFieldValues: {
    custom_field_id: number,
    type: string,
    value?: any, file_ids?: any
  }[] = [];
  @Input() textContainerStyleConfig: {
    marginTop?: string,
    marginBottom?: string,
  } = {};
  @Input() titleStyleConfig: {
    color?: string,
    fontSize?: string,
    fontWeight?: string,
  } = {};
  @Input() labelStyleConfig: {
    color?: string,
    fontSize?: string,
    fontWeight?: string,
  } = {};
  @Input() parentField: CustomFieldModel = null;
  @Input() grandParentField: CustomFieldModel = null;
  @Input() surveyGlobalStep: number;
  @Input() customEventIndex: number = null;

  @Output() radioButtonChangeEvent = new EventEmitter<{
    customField: CustomFieldModel,
    customFieldOption: CustomFieldOptionModel,
    oldCustomFieldDependencyIds: number[],
    removeOldCustomFieldDependencyIds: boolean,
    entityIndex?: number
  }>();

  @Output() changeCustomFieldSelectedValueEvent = new EventEmitter<{ [key: string]: any }>();
  @Output() blurCustomFieldSelectedValueEvent = new EventEmitter<{ [key: string]: any }>();
  // Emitted from preSelectStore()'s worker-mismatch cleanup when an already-given worker-select
  // answer is silently dropped because the newly-selected store doesn't match it — lets an
  // ancestor re-surface the (now-filtered) worker question instead of letting a required answer
  // vanish with no user feedback.
  // Carries the id of the worker-select question whose answer was invalidated, or undefined to
  // mean "whichever worker question this host renders" (the preSelectStore() path, which drops
  // every worker-select entry from the draft by type). Without the id a host holding two worker
  // questions clears the first one it finds, which need not be the one that went stale.
  @Output() workerSelectAnswerInvalidated = new EventEmitter<number | undefined>();

  selectedFiles: string[] = [];
  lang: string;
  selectedDate: Date[] = [];
  isEditAllowed = true;
  youtubeInfoUrlDynamicElements: any[] = [];
  queryParamCustomFieldValues: { id?: number, value?: any, ignore?: boolean }[] = [];
  columnRangeCount = 1;
  columnRangeArray: number[] = [];
  imageSelectEntitySelector = 0;
  validImageSelect = true;
  endOfProcessMessageObj: { [key: string]: any } = {}
  widgetConf: WidgetConfModel;
  waitForFileUpload = {};
  workers: WorkerDbModel[] = [];
  // The unscoped bookable pool the store scope is re-derived from (see applyStoreScopeToWorkers).
  private bookableWorkers: WorkerDbModel[] = [];
  // A restored answer re-loads its child question at most once per mount - and the latch is load
  // bearing, not belt-and-braces. There are TWO callers: loadOptionDependentQuestions(), which
  // ngOnInit calls once, and the getWorkers() handler, which deliberately RESETS this to re-load
  // the child after a cold pool repairs the advisor row. Without the latch that second path would
  // re-fetch on every pool response.
  private childQuestionRestored = false;
  // Whether that pool has actually resolved. An empty pool is ambiguous — it means "no bookable
  // workers" only AFTER the fetch lands; before that (and permanently after it errors) it means
  // "unknown", and pruning against it would drop answers that are perfectly valid.
  private workersLoaded = false;
  private readonly destroyRef = inject(DestroyRef);
  stores: StoreDbModel[] = [];
  egalWerShowSetting = true;
  maxFileSize = 1048576 * 5; // 5MB

  constructor(
    private translateService: TranslateService,
    private dateUtilService: DateUtilService,
    public widgetService: WidgetService,
    private customFieldsService: CustomFieldsService,
    private surveyService: SurveyService,
    private swRouteService: SwRouteService,
    private matDialog: MatDialog,
    private elementRef: ElementRef,
    public helperService: HelperService,
    private workerService: WorkerService,
    public bookingService: BookingService
  ) {
    this.widgetConf = this.widgetService.widgetConf
    this.lang = this.translateService.getFallbackLang();
    this.translateService.onLangChange.subscribe(language => this.lang = language.lang);
    this.queryParamCustomFieldValues = this.swRouteService.getCustomFieldValues();
  }

  ngOnInit(): void {
    this.egalWerShowSetting = !!Number(this.bookingService.partnerData?.egal_wer_show);

    this.columnRangeCount = this.parentField?.generate_entity_count ? this.parentField.generate_entity_count : 1;
    this.columnRangeArray = this.helperService.createArrayRange(this.columnRangeCount);
    this.endOfProcessMessageObj = {
      end_of_process_message: this.widgetService.widgetConf.end_of_process_message,
      is_multi_language: this.widgetService.widgetConf.is_multi_language,
      _translations: this.widgetService.widgetConf._translations
    }

    this.isEditAllowed = (this.customField.is_read_only === 0);

    if (!this.customField.customerSelectedValue) {
      this.customFieldsService.prefillCustomFieldSelectedValue(this.customField, []);
    }

    if (!this.customField.customerSelectedValue) {
      this.customField.customerSelectedValue = [];
    }

    if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_FILE) {
      this.updateMimeTypes();
      if (!this.customField.customerSelectedInputValue) { this.customField.customerSelectedInputValue = []; }
      if (!this.customField.customerSelectedFileValue) { this.customField.customerSelectedFileValue = []; }
    }

    const count = (this.parentField && this.parentField?.generate_entity_count ? this.parentField?.generate_entity_count : 1);
    for (let index = 0; index < count; index++) {
      if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_FILE) {
        this.customField.customerSelectedInputValue[index] = [];
        this.customField.customerSelectedFileValue[index] = [];
        this.customField.customerSelectedValue[index] = [];
        this.waitForFileUpload[index] = false;
      } else {
        this.customField.customerSelectedValue[index] = (this.customField.customerSelectedValue[index]
          ? this.customField.customerSelectedValue[index]
          : null);
      }
    }

    let foundedQueryParamCustomField: { id?: number, value?: any, ignore?: boolean } = {};

    if (this.queryParamCustomFieldValues?.length > 0) {
      foundedQueryParamCustomField = this.queryParamCustomFieldValues.find(
        (item: { id?: number, value?: any, ignore?: boolean }) => {
          return item.id === this.customField.id;
        }
      );
    }

    if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_NUMBER_SELECT_CARD) {
      this.setDefaultNumberCard();
    } else if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_WORKER_SELECT) {
      this.getWorkers();
      // Re-apply the store scope whenever a store-select answer changes it. On the stepped
      // questions view this never fires before getWorkers() has run (one question mounts at a
      // time); on hosts that mount every question at once it is what makes the filter work at all.
      this.bookingService.whenStoreContextChanges()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.getWorkers());
    } else if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_STORE_SELECT) {
      this.getStores();
    }

    if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_DATE) {
      this.customField.customerSelectedValue.forEach((item: any, index: number) => {
        if (item) {
          this.setSelectedDate(item, index);
        }
      });
    }

    if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_RADIO) {
      if (this.customField.customerSelectedValue) {
        if (this.customField.custom_field_options?.length > 0) {
          const foundedCFOption: CustomFieldOptionModel = this.customField.custom_field_options.find((item: CustomFieldOptionModel) => {
            return item.id === this.customField.customerSelectedValue;
          });
          if (foundedCFOption) {
            this.onRadioValueChanged(foundedCFOption, false);
          }
        }
      }
    }

    if ([
      CUSTOM_FIELD_TYPES.CUSTOM_FIELD_RADIO,
      CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT_CARD,
      CUSTOM_FIELD_TYPES.CUSTOM_FIELD_IMAGE_SELECT_CARD
    ].includes(this.customField.type)) {
      const optionsCopy = this.restrictOptionsToPreSelectedAdvisor(
        JSON.parse(JSON.stringify(this.customField.custom_field_options))
      );
      const innerCount = (this.parentField && this.parentField?.generate_entity_count ? this.parentField?.generate_entity_count : 1);
      this.customField.custom_field_options = [];
      for (let index = 0; index < innerCount; index++) {
        this.customField.custom_field_options[index] = JSON.parse(JSON.stringify(
          this.customField.alphabetically_sorted === 1
            ? this.helperService.sortByNumber(optionsCopy, 'label')
            : this.helperService.sortByNumber(optionsCopy, 'position')
        ));
      }
    }

    if (!foundedQueryParamCustomField?.ignore && foundedQueryParamCustomField?.id) {
      LoggerService.log('Loading custom field data from queryarams ');
      this.prefillCustomFieldValueFromQueryParams(foundedQueryParamCustomField.value);
    } else if (this.selectedCustomFieldValues?.some(item => item.custom_field_id === this.customField.id)) {
      LoggerService.log('Loading data from selectedCustomFieldValues ');
      this.prefillCustomFieldValue();
    } else {
      // Partner-configured prefill for booking questions (context.booking_questions_prefill).
      // Applies when this field has neither a query-param value nor its OWN restored draft
      // entry — a draft saved for other questions must not suppress this field's configured
      // default (the draft-array length check is global, so it has to be gated per field).
      // Reuse the query-param prefill path so the raw configured value is coerced into the
      // correct per-type customerSelectedValue shape (scalar for select/radio, [id] for
      // card/image-select, boolean for checkbox, array for multi-select).
      const contextPrefill = this.widgetService.widgetConf?.context?.booking_questions_prefill?.[this.customField.id];
      if (contextPrefill !== undefined && contextPrefill !== null && contextPrefill !== '') {
        this.prefillCustomFieldValueFromQueryParams(contextPrefill);
      }
    }

    this.handleAutoShowDependencies();

    // BEFORE loadOptionDependentQuestions(), which is what asks the host for the child question.
    // The prefill above is what puts a restored answer on the field, so a worker pool that came
    // back from cache before it ran saw nothing to re-derive; re-attempt now that the answer
    // exists. Doing it here means the warm path has the advisor in the state before the child is
    // ever requested, so that path needs no second fetch. The method is idempotent, so the async
    // first load having done it already costs nothing.
    if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_WORKER_SELECT && this.workersLoaded) {
      this.restoreWorkerScopeFromAnswer();
    }

    this.loadOptionDependentQuestions();
  }

  /**
   * The appointment services an option books, or an empty list when it books none.
   *
   * The value is stored as a JSON string. Anything that is not a JSON array — including the
   * `"48964,48965"` shape the dashboard writes — is treated as "names no service", so the option
   * survives the restriction below rather than being hidden by a value nobody can read.
   */
  private supportedServiceIdsOf(option: CustomFieldOptionModel): number[] {
    const raw = option?.supported_appointment_service_ids;
    if (!raw) {
      return [];
    }
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Array.isArray(parsed) ? parsed.map(Number).filter(id => !isNaN(id)) : [];
    } catch {
      return [];
    }
  }

  /**
   * Restrict a question's options to the services the pre-selected advisor actually provides.
   *
   * An option can name the appointment service it books. When the customer has already named an
   * advisor through a worker-select question, offering an option whose service that advisor cannot
   * perform is a dead end: picking it pre-decides a service, the services step then finds the
   * advisor offers it and falls back to the unrestricted list, and the booking silently rebinds to
   * whoever does provide it.
   *
   * Options that name no service always survive, so a catch-all stays reachable. The whole list
   * survives when the advisor provides none of them — an empty required question is worse than an
   * unfiltered one. Mirrors the advisor restriction in services-selection.component.ts.
   */
  private restrictOptionsToPreSelectedAdvisor(options: CustomFieldOptionModel[]): CustomFieldOptionModel[] {
    const state = this.bookingService?.selectedBookingData?.currentSelectedAppointmentState;
    const serviceWorkers = state?.selectedWorker?.services_workers;
    if (
      !state?.storeAndWorkerPreSelectedThroughWorkerSelectField ||
      !(state?.selectedWorkerId > 0) ||
      !serviceWorkers?.length ||
      !options?.length ||
      // Only the question that hangs off the advisor question itself. Without this, ANY
      // service-linked card-select anywhere in the partner's flow would be filtered the moment an
      // advisor was named - including questions that have nothing to do with that advisor, at
      // partners who never asked for the behaviour. The parent link is the semantic intent: the
      // advice-topic question is a CHILD of the advisor question.
      !state.workerSelectQuestionId ||
      this.customField.parent_custom_field_id !== state.workerSelectQuestionId
    ) {
      return options;
    }

    const advisorServiceIds = new Set(serviceWorkers.map(serviceWorker => Number(serviceWorker.appointment_service_id)));
    const restricted = options.filter(option => {
      const supported = this.supportedServiceIdsOf(option);
      return supported.length === 0 || supported.some(id => advisorServiceIds.has(id));
    });

    if (restricted.length === options.length) {
      return options;
    }

    if (!restricted.length) {
      LoggerService.warn(
        '[worker-select] Pre-selected advisor provides none of these options — falling back to the unrestricted list',
        state.selectedWorkerId
      );
      return options;
    }

    LoggerService.warn(
      '[worker-select] Restricting question options to the pre-selected advisor:',
      state.selectedWorkerId
    );
    return restricted;
  }

  /**
   * The store to scope the advisor fetch to, or null to fetch the whole partner.
   *
   * Gated on storePreSelectedThroughStoreSelectField, so this narrows only when a preceding
   * store-select QUESTION answered it - the same condition under which
   * applyStoreScopeToWorkers() would narrow the list client-side anyway. A store arriving from
   * anywhere else keeps the unscoped call.
   */
  private answeredStoreScopeId(): number | null {
    const state = this.bookingService.selectedBookingData?.currentSelectedAppointmentState;
    return state?.storePreSelectedThroughStoreSelectField && state?.selectedStoreId
      ? Number(state.selectedStoreId)
      : null;
  }

  /**
   * Whether the worker's store may be offered in the widget at all.
   *
   * The unscoped fetch (GET workers) hydrates `worker.store`, so the flag is read straight off
   * the row. The scoped fetch (POST workers/filter) does NOT contain Stores - and an absent key
   * makes `=== 0` false for EVERY row, which would empty the dropdown silently. It does not need
   * to: a scoped response only ever contains workers of the store that was answered, so the
   * question collapses to "is THAT store deeplink-only", read from the store object
   * preSelectStore() already put in the appointment state.
   */
  private isStoreBookable(worker: WorkerDbModel, requestedScope: number | null): boolean {
    if (worker?.store) {
      return worker.store.only_accessible_through_deeplink === 0;
    }

    if (requestedScope === null) {
      // Unscoped and no store on the row: nothing to exclude it by.
      return true;
    }

    const selectedStore = this.bookingService.selectedBookingData
      ?.currentSelectedAppointmentState?.selectedStore;

    return Number(selectedStore?.only_accessible_through_deeplink) !== 1;
  }

  getWorkers(): void {
    // Pin the scope this request is being issued for. Two store changes inside one round trip
    // produce two requests with DIFFERENT scopes, which the service's in-flight sharing does not
    // collapse; if the older one answers last it would install a stale pool, and
    // applyStoreScopeToWorkers() would then prune the customer's still-valid answer against it.
    const requestedScope = this.answeredStoreScopeId();
    this.customField.isOptionsAreLoading = true;
    this.workerService.getWorkers(requestedScope ?? undefined).subscribe({
      next: (workers => {
        if (requestedScope !== this.answeredStoreScopeId()) {
          // A newer store answer already re-entered getWorkers(); that request owns the loading
          // flag and the pool, so drop this reply rather than overwriting them.
          return;
        }
        // Pool for the CURRENT store scope. A later store change re-enters getWorkers() rather
        // than re-filtering this list - re-narrowing an already-scoped pool would permanently
        // drop the new store's advisors.
        // The predicate is the worker step's rule, so the question cannot offer an advisor the
        // step hides; `bookable` is coerced because the API may serialise it as a string, while
        // is_directly_bookable is a real boolean (tinyint(1) -> CakePHP BoolType).
        this.bookableWorkers = workers.filter(worker =>
          // GET workers filters Workers.status = 1 in its finder; POST workers/filter does not,
          // and `status` is not in the Filter allowlist so the request body cannot restore it.
          // Without this, a deactivated worker whose bookable flags were never cleared reaches
          // the dropdown on the scoped path only. Unscoped rows are all status 1 already, so
          // this leaves that path byte-for-byte unchanged.
          Number(worker?.status) === 1 &&
          Number(worker?.bookable) === 1 &&
          worker?.is_directly_bookable === true &&
          this.isStoreBookable(worker, requestedScope)
        );
        this.workersLoaded = true;
        this.applyStoreScopeToWorkers();
        // After the prune, so a restored answer is only re-derived when it survived it.
        const repaired = this.restoreWorkerScopeFromAnswer();

        // COLD POOL. getWorkers() answers synchronously from cache when it is warm, so on the
        // ordinary Back the advisor is in the state long before the child question mounts. On a
        // cache MISS this handler runs late - and the child, which the option restriction lives
        // in, may already have mounted against the service-derived advisor row that carries no
        // services_workers. The restriction fails OPEN there and never re-derives, so the customer
        // is offered topics their advisor cannot serve: the very defect this ships to fix.
        // Re-load the child through the same path an advisor CHANGE already uses, but only when
        // the row was really repaired - a no-op repair must not cost a second fetch.
        if (repaired && this.childQuestionRestored) {
          this.childQuestionRestored = false;
          this.restoreChildQuestionFromAnswer();
        }
        this.customField.isOptionsAreLoading = false;
      }),
      error: ((error: HttpErrorResponse) => {
        // Same stale-scope rule as the next-handler: a request for a store the customer has
        // already moved past must not clear the loading flag the NEWER request owns, or the
        // spinner vanishes while that one is still in flight. The error is always logged.
        if (requestedScope === this.answeredStoreScopeId()) {
          this.customField.isOptionsAreLoading = false;
        }
        LoggerService.error(error);
      })
    });
  }

  // Combination filter: when a store-select question has pre-selected a store, restrict the
  // worker list to that store's workers. Guarded on the store-select flag so partners without a
  // store-select question see byte-identical worker-select behavior (regression guard).
  // Idempotent and re-runnable — it always starts from the unscoped bookableWorkers pool.
  private applyStoreScopeToWorkers(): void {
    // Copy: helperService.sortBy() sorts in place and returns the SAME reference, so assigning it
    // straight to this.workers would alias the rendered list onto the pool this method exists to
    // keep pristine.
    let workers = [...this.bookableWorkers];

    const currentSelectedAppointmentState = this.bookingService.selectedBookingData.currentSelectedAppointmentState;
    const selectedStoreId = currentSelectedAppointmentState?.selectedStoreId;
    if (currentSelectedAppointmentState?.storePreSelectedThroughStoreSelectField && selectedStoreId) {
      // workerServesStore() rather than a raw worker.store_id compare: with the multi-store
      // addon a worker serves several stores via worker.stores[] and store_id no longer
      // reflects all of them, so a scalar compare drops workers who do serve the selected
      // store. Same helper the equivalent filter in workers-selection.component.ts uses.
      workers = workers.filter(worker => workerServesStore(worker, Number(selectedStoreId)));
      if (this.pruneWorkersOutsideStore(workers)) {
        const draft = this.bookingService.selectedBookingData.selectedCustomFieldValues || [];

        if (this.hasRemainingWorkerAnswer()) {
          // PARTIAL prune: some advisors do serve the store and are still on screen. Narrow the
          // draft entry to the survivors rather than deleting it — deleting it here while the
          // ng-select keeps rendering them is how the two stores of truth end up disagreeing,
          // which only shows itself on a reload. And do NOT hand off to the host: its handler
          // blanks the whole question, undoing the prune we just made selective.
          // The entry legitimately does not exist yet when the customer answered but has not
          // navigated past the question, so setSelectedCustomFieldValues() never created it.
          // Persist only when there was something to narrow, so "something was persisted" can
          // never be true while "the entry was narrowed" is false.
          const entry = draft.find(value => value.custom_field_id === this.customField.id);
          if (entry) {
            entry.value = (this.customField.customerSelectedValue || [])
              .flat()
              .filter(id => id !== null && id !== undefined && id !== '');
            // false: getSupportedIds() reads supported ids off custom_field_options, which a
            // worker-select question has none of, so this prune cannot change hidden-step
            // resolution — re-arming could only re-hide a step the customer already passed.
            this.bookingService.setCustomFieldValuesInLocalStorage(draft, false);
          }
        } else {
          // Nothing survived. Mirror preSelectStore()'s cleanup: drop the entry from the
          // persisted draft and let an ancestor re-surface the question, instead of the customer
          // meeting an empty required field at submit time. This is the only clearing path for a
          // [multiple] worker question — onSelectChange receives an array there, so
          // preSelectWorkerAndStore()'s `if (worker)` guard no-ops and preSelectStore()'s own
          // cleanup never sees a previouslySelectedWorker. The id lets a host rendering two
          // worker questions clear the right one.
          this.bookingService.selectedBookingData.selectedCustomFieldValues =
            draft.filter(value => value.custom_field_id !== this.customField.id);
          this.bookingService.setCustomFieldValuesInLocalStorage(this.bookingService.selectedBookingData.selectedCustomFieldValues, false);
          this.workerSelectAnswerInvalidated.emit(this.customField.id);
        }
      }
    }

    this.workers = this.helperService.sortBy(workers, 'resource_name');
  }

  // Whether any advisor pick survives across every slot. Distinguishes a partial prune (some ids
  // still serve the store — keep them, don't let the host blank the question) from a total one.
  // Deliberate trade-off: a surviving id in ANY slot counts, so a multi-entity answer where one
  // entity was emptied and another kept still reports "remaining" and stays silent. Pinned by a
  // spec case rather than left to be rediscovered.
  private hasRemainingWorkerAnswer(): boolean {
    return (this.customField.customerSelectedValue || []).some(slot =>
      Array.isArray(slot) ? slot.length > 0 : slot !== null && slot !== undefined && slot !== ''
    );
  }

  // Self-heal: a previously-answered worker may not belong to the newly-selected store — drop the
  // stale pick so a worker outside the filtered list is never submitted. Every slot is checked,
  // not just index 0: the same template renders worker-select with [multiple] (an array in one
  // slot) and one ng-select per columnRangeArray entity (one slot per entity), so a scalar-only
  // check either wipes a whole multi-selection via Number(array)=NaN or silently keeps a stale
  // worker at index 1..n.
  // Never touch the "Anybody" sentinel — it means "no specific worker", not a stale pick.
  // Sentinel literal: onSelectChange's own WORKER_SELECT branch checks `event === '0'`, but the
  // "Anybody" <ng-option> above is an UNBOUND `value="null"` attribute, which Angular always
  // treats as the literal string 'null' (never the JS `null`) — so the value actually written to
  // customerSelectedValue is the string 'null', not '0'. Guarding on both keeps this correct
  // regardless of which convention is authoritative (pre-existing '0' vs 'null' mismatch in
  // onSelectChange, not introduced by this diff — flagged separately, out of scope here).
  // Returns true when it actually dropped something, so the caller can mirror preSelectStore()'s
  // full cleanup rather than silently mutating the in-memory answer.
  private pruneWorkersOutsideStore(scopedWorkers: WorkerDbModel[]): boolean {
    // An unresolved pool is "unknown", not "nobody serves this store": pruning against it would
    // null answers that are perfectly valid. The list re-derivation above may run at any time,
    // but this destructive pass waits until getWorkers() has actually landed — which also covers
    // the fetch-error case, where the pool stays empty for good.
    if (!this.workersLoaded) {
      return false;
    }

    const selectedValues = this.customField.customerSelectedValue;
    if (!selectedValues?.length) {
      return false;
    }

    let pruned = false;

    const isAnybodySentinel = (value: unknown): boolean =>
      value === '0' || value === 0 || value === 'null';
    const servesStore = (value: unknown): boolean =>
      scopedWorkers.some(worker => worker.id === Number(value));

    for (let index = 0; index < selectedValues.length; index++) {
      const slot = selectedValues[index];
      if (slot === null || slot === undefined || slot === '') {
        continue;
      }

      if (Array.isArray(slot)) {
        // Multi-select slot: prune per worker id rather than wiping the whole selection.
        const kept = slot.filter(id => isAnybodySentinel(id) || servesStore(id));
        if (kept.length !== slot.length) {
          selectedValues[index] = kept;
          pruned = true;
        }
        continue;
      }

      if (!isAnybodySentinel(slot) && !servesStore(slot)) {
        selectedValues[index] = null;
        pruned = true;
      }
    }

    return pruned;
  }

  getStores(): void {
    this.customField.isOptionsAreLoading = true;
    this.bookingService.getStoresWithWorkers(null).subscribe({
      next: (stores => {
        if (!this.widgetService?.widgetConf?.context?.is_internal) {
          stores = stores.filter(store => store.has_only_internal_appointment_service === 0);
        }
        this.stores = stores;
        this.customField.isOptionsAreLoading = false;
        // The list is the only thing that can turn a restored store ID back into the store row
        // the appointment state needs, so the re-application waits for it.
        this.restoreStoreScopeFromAnswer();
      }),
      error: ((error: HttpErrorResponse) => {
        this.customField.isOptionsAreLoading = false;
        LoggerService.error(error);
      })
    });
  }

  handleAutoShowDependencies(): void {
    if (
      this.customField.is_multiple_select === 0 &&
      this.customField.is_entity_generator &&
      this.customField.customerSelectedValue && this.customField.customerSelectedValue?.length > 0
    ) {
      const foundOption = this.customField.custom_field_options[0]
        .find((option: CustomFieldOptionModel) => option.id === Number(this.customField.customerSelectedValue[0]));

      switch ((foundOption ? this.customField.type : null)) {
        case CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT:
        case CUSTOM_FIELD_TYPES.CUSTOM_FIELD_IMAGE_SELECT_CARD:
        case CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT_CARD:
          if (foundOption) {
            this.onCardImageCustomField(null, foundOption, 0);
          }
          return;
        case CUSTOM_FIELD_TYPES.CUSTOM_FIELD_RADIO:
          if (foundOption) {
            this.onRadioValueChanged(foundOption, true, 0);
          }
          return;
      }
    }
  }

  loadOptionDependentQuestions(): void {
    if (
      this.customField.type !== this.customFieldConst.CUSTOM_FIELD_WORKER_SELECT &&
      this.customField.is_entity_generator === 0 &&
      this.customField?.custom_field_options && this.customField?.custom_field_options?.length > 0 &&
      this.customField.customerSelectedValue && this.customField.customerSelectedValue?.length > 0
    ) {
      const options = Array.isArray(this.customField.custom_field_options[0]) ? this.customField.custom_field_options[0] : this.customField.custom_field_options;
      const hasOptionDependencies = options.filter(option => option.dependencies !== null);
      if (hasOptionDependencies.length > 0) {
        const entityIndex = this.customField.customerSelectedValue.findIndex((element: any) => element!)
        if (entityIndex >= 0) {
          const ids = this.customField.customerSelectedValue[entityIndex];
          if (ids) {
            switch (this.customField.type) {
              case CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT:
                this.onSelectChange(ids, entityIndex);
                break;
              case CUSTOM_FIELD_TYPES.CUSTOM_FIELD_IMAGE_SELECT_CARD:
              case CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT_CARD:
                if (!this.customField.is_multiple_select) {
                  const foundOption = hasOptionDependencies.find(option => Number(option.id) === Number(ids[0]));
                  if (foundOption) {
                    this.onCardImageCustomField(null, foundOption, 0);
                  }
                }
                break;
              case CUSTOM_FIELD_TYPES.CUSTOM_FIELD_RADIO:
                // this.onRadioValueChanged(foundOption, true, 0);
                break;
            }
          }
        }
      }

    } else if (
      this.customField.type === this.customFieldConst.CUSTOM_FIELD_STORE_SELECT ||
      this.customField.type === this.customFieldConst.CUSTOM_FIELD_WORKER_SELECT
    ) {
      // The field-level twin of the option replay above. These two types declare their dependent
      // question on the FIELD (child_custom_field_id) and carry no options at all, so the arm
      // above can never reach them and a restored answer left the child unloaded.
      //
      // Driven from here rather than from the store/worker restore helpers on purpose. This runs
      // once per mount, after the prefill, and needs nothing resolved: the "anybody" sentinel
      // ("Person is not listed") never matches a worker row, so a child load gated on resolving
      // the answer loses the entire sub-chain for every customer who picks it.
      this.restoreChildQuestionFromAnswer();
    }

    // A worker-select answer used to be thrown away here on every re-mount, together with the
    // WHOLE appointment state (`new CurrentSelectedAppointmentState()`), which is why pressing
    // Back into the questions step lost the advisor, the store scope and every answer below them.
    // It was a blunt "do not trust a restored worker answer" guard from the original worker-select
    // implementation, written when nothing else re-validated the answer - note the dependency
    // re-emit that sat under it was already commented out, so the child question could never come
    // back either.
    // That job is now done properly by applyStoreScopeToWorkers(), which runs once the pool has
    // actually loaded and prunes the answer against it selectively - narrowing it to the advisors
    // who serve the answered store, or dropping it and emitting workerSelectAnswerInvalidated when
    // none do. Re-deriving a restored answer is restoreWorkerScopeFromAnswer()'s job.
  }

  bindYoutubeInfoUrlClickHandler(): void {
    setTimeout(() => {
      this.youtubeInfoUrlDynamicElements = this.elementRef.nativeElement.querySelectorAll('.select-option-youtube-element');
      if (this.youtubeInfoUrlDynamicElements.length > 0) {
        this.youtubeInfoUrlDynamicElements.forEach((element: any) => {
          element.addEventListener('click', () => {
            if (element.dataset?.option) {
              const custom_field_option = JSON.parse(element.dataset.option) as CustomFieldOptionModel;
              this.openYoutubeVideo(custom_field_option, null);
            }
          });
        });
      }
    }, 100);
  }

  unbindYoutubeInfoUrlClickHandler(): void {
    if (this.youtubeInfoUrlDynamicElements.length > 0) {
      this.youtubeInfoUrlDynamicElements.forEach((element: any) => {
        element.removeEventListener('click', null);
      });
    }
  }

  onSelectChange(event: any, entityIndex = 0): void {
    if (!this.isEditAllowed || this.deactivateField) {
      LoggerService.log('Editing is disabled');
      return;
    }

    LoggerService.log('select event is', event, this.customField);

    if (!this.customField.customerSelectedValue[entityIndex] || this.customField.customerSelectedValue[entityIndex]?.length === 0) {
      this.customField.customerSelectedValue[entityIndex] = [];
    }

    let oldCustomFieldDependencyIds: number[] = [];
    if (this.customField?.customFieldDependencies?.length > 0) {
      // Whole branch, not just direct children — the deepest answer carries the resolved service.
      // ⚠️ Must stay INSIDE the guard: that is what keeps the mount-time prefill cascade a no-op.
      oldCustomFieldDependencyIds = this.bookingService.getDescendantCustomFieldIds(this.customField);
    }

    const customFieldOption = this.customField.custom_field_options.filter((option: CustomFieldOptionModel) => {
      return Array.isArray(event) ? event.includes(option.id) : [event].includes(option.id);
    });
    this.customField.customFieldDependencies = [];
    const checkForOptionsDependentQuestions = customFieldOption.filter((field: CustomFieldOptionModel) => field.dependencies !== null);

    this.onChangeCustomFieldSelectedValue({
      entityIndex,
      checkForOptionsDependentQuestions: (checkForOptionsDependentQuestions.length > 0)
    });

    this.customField.showEndOfProcess = (customFieldOption?.is_end_of_process === 1);
    this.widgetService.endOfProcessSurvey = {
      step: this.surveyGlobalStep,
      status: this.customField.showEndOfProcess
    };

    if (
      this.customField.showEndOfProcess === false &&
      this.customField?.is_entity_generator === 1 &&
      this.customField?.is_multiple_select === 0
    ) {
      this.handleEntityGenerator(customFieldOption);

      this.radioButtonChangeEvent.emit({
        customField: this.customField,
        customFieldOption,
        oldCustomFieldDependencyIds,
        removeOldCustomFieldDependencyIds: true,
        entityIndex
      });
    } else if (this.customField.showEndOfProcess === false) {
      this.customField.generate_entity_count = 0;
      this.radioButtonChangeEvent.emit({
        customField: this.customField,
        customFieldOption,
        oldCustomFieldDependencyIds,
        removeOldCustomFieldDependencyIds: true,
        entityIndex
      });
    }

    if (this.customField.type === this.customFieldConst.CUSTOM_FIELD_WORKER_SELECT) {
      let foundWorker: WorkerDbModel;
      if (event === '0') {
        const anybodyWorkerIndex = Math.floor(Math.random() * this.workers.length);
        foundWorker = this.workers[anybodyWorkerIndex];
      } else {
        foundWorker = this.workers.find(worker => worker.id === event);
      }

      if (foundWorker) {
        this.preSelectWorkerAndStore(foundWorker);
      } else {
        // The answer resolves to no advisor - "the person is not listed", which the template
        // renders as an unbound value="null" so ng-select emits the STRING 'null', or any other
        // value that matches no row. preSelectWorkerAndStore() no-ops on a falsy worker, which
        // used to be harmless because nothing read the leftover advisor. It is not harmless now:
        // the option restriction and the store step's advisor guard both read selectedWorker, so
        // a customer who picks a real advisor and then changes to "not listed" would keep being
        // offered that advisor's topics and could still be BOOKED with them.
        this.clearPreSelectedWorker();
      }
    }

    if (this.customField.type === this.customFieldConst.CUSTOM_FIELD_STORE_SELECT) {
      // ng-select emits an ARRAY when the question carries is_multiple_select, and a store answer
      // drives exactly one store scope. Matching the raw event would never find a store, drop into
      // the clear branch below and wipe selectedStoreId on every pick -- so unwrap to the first id
      // first. An emptied multi-select yields [] -> undefined -> the clear branch, which is right.
      const pickedStoreId = Array.isArray(event) ? event[0] : event;
      const foundStore = this.stores.find(store => store.id === pickedStoreId);
      if (foundStore) {
        this.preSelectStore(foundStore);
      } else {
        // User cleared the ng-select (no "Anybody" equivalent for store-select, OQ-4).
        this.clearPreSelectedStore();
      }
    }
  }

  onCheckBoxFieldChange(value: boolean): void {
    LoggerService.log('on checkbox field change ', value, this.customField);

    if (!this.customField.showEndOfProcess) {
      this.radioButtonChangeEvent.emit({
        customField: this.customField,
        customFieldOption: null,
        oldCustomFieldDependencyIds: [],
        removeOldCustomFieldDependencyIds: true,
      });
    }

    this.onChangeCustomFieldSelectedValue();
  }

  onRadioValueChanged(item: CustomFieldOptionModel, removeOldCustomFieldDependencyIds: boolean, entityIndex = 0): void {
    if (!this.isEditAllowed) {
      LoggerService.warn('Radio button is disabled');
      this.customField.customerSelectedValue[entityIndex] = undefined;
      return;
    }

    let oldCustomFieldDependencyIds: number[] = [];
    if (this.customField?.customFieldDependencies?.length > 0) {
      // Whole branch, not just direct children — the deepest answer carries the resolved service.
      // ⚠️ Must stay INSIDE the guard: that is what keeps the mount-time prefill cascade a no-op.
      oldCustomFieldDependencyIds = this.bookingService.getDescendantCustomFieldIds(this.customField);
    }
    this.customField.customerSelectedValue[entityIndex] = item.id;
    this.customField.customFieldDependencies = [];
    this.onChangeCustomFieldSelectedValue(item);

    this.customField.showEndOfProcess = (item?.is_end_of_process === 1);
    this.widgetService.endOfProcessSurvey = {
      step: this.surveyGlobalStep,
      status: this.customField.showEndOfProcess
    };

    if (this.customField.showEndOfProcess === false) {
      this.radioButtonChangeEvent.emit({
        customField: this.customField,
        customFieldOption: item,
        oldCustomFieldDependencyIds,
        removeOldCustomFieldDependencyIds,
        entityIndex
      });
    }
  }

  onChangeDate(event: MatDatepickerInputEvent<any>, entityIndex: number): void {
    const format = (this.customField.format ? this.customField.format.toUpperCase() : this.dateFormat);
    this.customField.customerSelectedValue[entityIndex] = this.dateUtilService.getFormattedDateByFormat(event.value, format);
    this.onChangeCustomFieldSelectedValue();
  }

  prefillCustomFieldValue(): void {
    const foundedValue: {
      custom_field_id: number,
      type: string,
      value?: any, file_ids?: any, option_values?: {
        custom_field_option_id: number,
        custom_field_option_value: number
      }[]
    } = this.selectedCustomFieldValues.find((item: {
      custom_field_id: number,
      type: string,
      value?: any, file_ids?: any, option_values?: {
        custom_field_option_id: number,
        custom_field_option_value: number
      }[]
    }) => {
      return item.custom_field_id === this.customField.id;
    });

    if (!foundedValue) {
      return;
    }

    if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT
      || this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_WORKER_SELECT
      || this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_STORE_SELECT) {
      if (this.customField.is_multiple_select === 1) {
        this.customField.customerSelectedValue[0] = foundedValue.value;
      } else {
        this.customField.customerSelectedValue[0] = (foundedValue.value && foundedValue.value[0]) ? foundedValue.value[0] : undefined;
      }
    } else if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_FILE) {
      this.customField.customerSelectedValue[0] = foundedValue.file_ids;
      this.getFilesByFileIds(foundedValue.file_ids);
    } else if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_RADIO) {
      this.customField.customerSelectedValue[0] = Number(foundedValue.value);
      if (this.customField.custom_field_options?.length > 0) {
        const foundedCFOption: CustomFieldOptionModel = this.customField.custom_field_options?.[0]?.find((item: CustomFieldOptionModel) => {
          return item.id === this.customField.customerSelectedValue?.[0];
        });
        if (foundedCFOption) {
          this.onRadioValueChanged(foundedCFOption, false);
        }
      }
    } else if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_DATE) {
      this.customField.customerSelectedValue[0] = foundedValue.value;
      this.setSelectedDate(foundedValue.value);
    } else if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_CHECKBOX) {
      if (foundedValue.value === true || foundedValue.value === 'true') {
        this.customField.customerSelectedValue[0] = true;
      } else {
        this.customField.customerSelectedValue[0] = false;
      }
    } else if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_NUMBER_SELECT_CARD) {
      if (!this.customField?.customerSelectedValue) {
        this.customField.customerSelectedValue[0] = [];
      }
      if (foundedValue?.option_values?.length > 0) {
        this.customField.customerSelectedValue[0] = foundedValue?.option_values;
      }

      for (const option of this.customField.custom_field_options[0]) {
        const foundedItem: {
          custom_field_option_id: number,
          custom_field_option_value: number
        } = this.customField.customerSelectedValue[0].find((item: {
          custom_field_option_id: number,
          custom_field_option_value: number
        }) => {
          return option.id === Number(item.custom_field_option_id);
        });
        LoggerService.log('foundedItem ', foundedItem);
        if (foundedItem) {
          option.option_value = foundedItem.custom_field_option_value;
          /*widgetQuestion.customerSelectedValue.push({
            custom_field_option_id: option.id,
            custom_field_option_value: Number(option.option_value)
          });*/
        } else {
          option.option_value = option.number_default;
        }
      }
    } else {
      this.customField.customerSelectedValue[0] = foundedValue.value;
    }
  }

  prefillCustomFieldValueFromQueryParams(value: any): void {
    if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT) {
      if (this.customField.is_multiple_select === 1) {
        if (Array.isArray(value)) {
          this.customField.customerSelectedValue[0] = value.map(id => Number(id));
        } else {
          this.customField.customerSelectedValue[0] = [Number(value)];
        }
      } else {
        this.customField.customerSelectedValue[0] = (value) ? Number(value) : undefined;
      }
    } else if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_IMAGE_SELECT_CARD || this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_SELECT_CARD) {
      if (this.customField.is_multiple_select === 1) {
        if (Array.isArray(value)) {
          this.customField.customerSelectedValue[0] = value.map(id => Number(id));
        } else {
          this.customField.customerSelectedValue[0] = [Number(value)];
        }
      } else {
        this.customField.customerSelectedValue[0] = (value) ? [Number(value)] : [];
      }
    } else if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_FILE) {
      // TODO currently file custom field is not supporting query params
    } else if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_RADIO) {
      this.customField.customerSelectedValue[0] = Number(value);
      if (this.customField.custom_field_options[0]?.length > 0) {
        const foundedCFOption: CustomFieldOptionModel = this.customField.custom_field_options[0].find((item: CustomFieldOptionModel) => {
          // customerSelectedValue is an array — match against its first entry, not the array
          // itself (id === [id] is always false), so a prefilled radio fires onRadioValueChanged
          // and loads/reveals its option-dependent child (matches the customerDetail path).
          return item.id === this.customField.customerSelectedValue?.[0];
        });
        if (foundedCFOption) {
          this.onRadioValueChanged(foundedCFOption, false);
        }
      }
    } else if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_DATE) {
      this.customField.customerSelectedValue[0] = value;
      this.setSelectedDate(value);
    } else if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_CHECKBOX) {
      if (value === true || value === 'true') {
        this.customField.customerSelectedValue[0] = true;
      } else {
        this.customField.customerSelectedValue[0] = false;
      }
    } else {
      this.customField.customerSelectedValue[0] = value;
    }

    LoggerService.log('Customer selected value this.customField.customerSelectedValue ', this.customField.customerSelectedValue);
  }

  setSelectedDate(date: string, entityIndex = 0): void {
    LoggerService.log('Inside setSelectedDate this.customField.format ', this.customField.format);
    if (this.customField.format) {
      this.selectedDate[entityIndex] = this.dateUtilService.getDateFromString(date, this.customField.format.toUpperCase());
    } else {
      this.selectedDate[entityIndex] = this.dateUtilService.getDateFromString(date, this.dateFormat);
    }
  }

  setDefaultNumberCard(): void {
    for (const option of this.customField.custom_field_options) {
      option.option_value = option.number_default;
    }
    const optionsCopy = JSON.parse(JSON.stringify(this.customField.custom_field_options));
    if (this.parentField && this.parentField?.generate_entity_count) {
      this.customField.custom_field_options = [];
      for (let index = 0; index < this.parentField.generate_entity_count; index++) {
        this.customField.custom_field_options[index] = JSON.parse(JSON.stringify(optionsCopy));
      }
    } else {
      this.customField.custom_field_options = [JSON.parse(JSON.stringify(optionsCopy))];
    }

    if (this.customField.customerSelectedValue?.length > 0) {
      for (const [index, values] of this.customField.custom_field_options.entries()) {
        for (const value of values) {
          LoggerService.log('[value]', value);
          let foundOption = this.customField.customerSelectedValue[index];
          if (foundOption) {
            foundOption = foundOption.find((option: any) => option.custom_field_option_id === value.id);
            if (foundOption) {
              value.option_value = foundOption.custom_field_option_value;
            }
          }
        }
      }
    }
  }

  onCardImageCustomField(event: MatCheckboxChange, option: CustomFieldOptionModel, entityIndex = 0): void {
    if (!this.isEditAllowed || this.deactivateField) {
      LoggerService.log('Editing is disabled');
      return;
    }

    LoggerService.log(
      'Inside onCardImageCustomField this.customField.customerSelectedValue',
      this.customField.customerSelectedValue[entityIndex]
    );

    if (!this.customField.customerSelectedValue[entityIndex] || this.customField.customerSelectedValue[entityIndex]?.length === 0) {
      this.customField.customerSelectedValue[entityIndex] = [];
    }

    if (!Array.isArray(this.customField.customerSelectedValue[entityIndex])) {
      if (this.customField.customerSelectedValue[entityIndex].indexOf(',') > -1) {
        this.customField.customerSelectedValue[entityIndex] = this.customField.customerSelectedValue[entityIndex]
          .split(',')
          .map((i: any) => Number(i));
      } else {
        this.customField.customerSelectedValue[entityIndex] = [Number(this.customField.customerSelectedValue[entityIndex])];
      }
    }

    if (this.customField.is_multiple_select === 1) {
      if (this.customField.customerSelectedValue[entityIndex]?.indexOf(option.id) > -1) {
        this.customField.customerSelectedValue[entityIndex] = this.customField.customerSelectedValue[entityIndex].filter((item: number) => {
          return item !== option.id;
        });
        option.customerSelectedValue = false;
      } else {
        this.customField.customerSelectedValue[entityIndex].push(option.id);
        option.customerSelectedValue = true;
      }
    } else {
      this.customField.customerSelectedValue[entityIndex] = [option.id];
      option.customerSelectedValue = true;
    }

    let oldCustomFieldDependencyIds: number[] = [];
    if (this.customField?.customFieldDependencies?.length > 0) {
      // Whole branch, not just direct children — the deepest answer carries the resolved service.
      // ⚠️ Must stay INSIDE the guard: that is what keeps the mount-time prefill cascade a no-op.
      oldCustomFieldDependencyIds = this.bookingService.getDescendantCustomFieldIds(this.customField);
    }
    this.customField.customFieldDependencies = [];
    this.onChangeCustomFieldSelectedValue(option);

    this.customField.showEndOfProcess = (option?.is_end_of_process === 1);
    this.widgetService.endOfProcessSurvey = {
      step: this.surveyGlobalStep,
      status: this.customField.showEndOfProcess
    };

    if (
      this.customField.showEndOfProcess === false &&
      this.customField?.is_entity_generator === 1 &&
      this.customField?.is_multiple_select === 0
    ) {
      this.handleEntityGenerator(option);

      this.radioButtonChangeEvent.emit({
        customField: this.customField,
        customFieldOption: option,
        oldCustomFieldDependencyIds,
        removeOldCustomFieldDependencyIds: true
      });
    } else if (this.customField.showEndOfProcess === false) {
      this.customField.generate_entity_count = 0;
      this.radioButtonChangeEvent.emit({
        customField: this.customField,
        customFieldOption: option,
        oldCustomFieldDependencyIds,
        removeOldCustomFieldDependencyIds: true
      });
    }

    if (this.customField.required === 1 && this.customField.is_read_only === 0) {
      const hasNullValues = this.customField.customerSelectedValue.find((answer: any) => answer === null);
      this.validImageSelect = (hasNullValues === null) ? false : true;
    }
  }

  addNumberCardValue(option: CustomFieldOptionModel, entityIndex = 0): void {
    if (!this.isEditAllowed || this.deactivateField) {
      LoggerService.log('Editing is disabled');
      return;
    }
    LoggerService.log('option ', option);
    if (Number(option?.option_value) < option.number_max) {
      option.option_value = Number(option?.option_value) + 1;
      if (!this.customField.customerSelectedValue[entityIndex] || this.customField.customerSelectedValue[entityIndex]?.length === 0) {
        this.customField.customerSelectedValue[entityIndex] = [];
      }
      if (this.customField.customerSelectedValue[entityIndex].length === 0) {
        this.customField.customerSelectedValue[entityIndex].push({
          custom_field_option_id: option.id,
          custom_field_option_value: option.option_value
        });
      } else {
        const foundedItem: { custom_field_option_id: number, custom_field_option_value: number } =
          this.customField.customerSelectedValue[entityIndex]
            .find((item: { custom_field_option_id: number, custom_field_option_value: number }) => {
              return item.custom_field_option_id === option.id;
            });
        if (foundedItem) {
          for (const optionValue of this.customField.customerSelectedValue[entityIndex]) {
            if (optionValue.custom_field_option_id === option.id) {
              optionValue.custom_field_option_value = option.option_value;
            }
          }
        } else {
          this.customField.customerSelectedValue[entityIndex].push({
            custom_field_option_id: option.id,
            custom_field_option_value: option.option_value
          });
        }
      }
    }
    this.onChangeCustomFieldSelectedValue(option);
  }

  reduceNumberCardValue(option: CustomFieldOptionModel, entityIndex = 0): void {
    if (!this.isEditAllowed || this.deactivateField) {
      LoggerService.log('Editing is disabled');
      return;
    }
    LoggerService.log('option ', option);
    if (Number(option?.option_value) > option.number_min) {
      option.option_value = Number(option?.option_value) - 1;
      if (!this.customField.customerSelectedValue[entityIndex] || this.customField.customerSelectedValue[entityIndex]?.length === 0) {
        this.customField.customerSelectedValue[entityIndex] = [];
      }
      if (this.customField.customerSelectedValue[entityIndex]?.length === 0) {
        this.customField.customerSelectedValue[entityIndex].push({
          custom_field_option_id: option.id,
          custom_field_option_value: option.option_value
        });
      } else {
        const foundedItem: { custom_field_option_id: number, custom_field_option_value: number } =
          this.customField.customerSelectedValue[entityIndex]
            .find((item: { custom_field_option_id: number, custom_field_option_value: number }) => {
              return item.custom_field_option_id === option.id;
            });
        if (foundedItem) {
          for (const optionValue of this.customField.customerSelectedValue[entityIndex]) {
            if (optionValue.custom_field_option_id === option.id) {
              optionValue.custom_field_option_value = option.option_value;
            }
          }
        } else {
          this.customField.customerSelectedValue[entityIndex].push({
            custom_field_option_id: option.id,
            custom_field_option_value: option.option_value
          });
        }
      }
    }
    this.onChangeCustomFieldSelectedValue(option);
  }

  onChangeCustomFieldSelectedValue(additionalEventData?: { [key: string]: any }): void {
    LoggerService.log('this.customField ', this.customField);
    let eventData: { [key: string]: any } = {};

    if (this.grandParentField?.generate_entity_count) {
      eventData.entityNames = this.grandParentField.entity_names;
    } else if (this.parentField?.generate_entity_count) {
      eventData.entityNames = this.parentField.entity_names;
    }

    if (additionalEventData) {
      eventData = { ...eventData, ...additionalEventData };
    }

    this.changeCustomFieldSelectedValueEvent.emit(eventData);
    this.swRouteService.changeCustomFieldStateToIgnore(this.customField.id);
  }

  handleEntityGenerator(option: CustomFieldOptionModel): void {
    if (this.customField.custom_field_options && this.customField.custom_field_options.length > 0) {
      this.customField.generate_entity_count = (option && option.number_of_columns)
        ? Number(option.number_of_columns)
        : 0;

      LoggerService.log('[generate_entity_count]', this.customField.generate_entity_count);

      if (!this.customField.entity_names) { this.customField.entity_names = []; }

      for (let index = 0; index < this.customField.generate_entity_count; index++) {
        this.customField.entity_names[index] = (this.customField.entity_names[index] ? this.customField.entity_names[index] : null);
      }
    }
  }

  onBlurCustomFieldSelectedValue(): void {
    const eventData: { [key: string]: any } = {};
    if (this.parentField && this.parentField.generate_entity_count) {
      eventData.entityNames = this.parentField.entity_names;
    }

    this.blurCustomFieldSelectedValueEvent.emit(eventData);
  }

  onFileChanged(evt: any, entityIndex = 0): void {
    const target = <DataTransfer>evt.target;

    if (!this.isEditAllowed || !target.files?.length) { return; }

    this.customFieldsService.imageUploaded = false;
    this.customField.errorMessage = {};

    let largeFileSize = false;
    let invalidFileType = false;

    if (Number(this.customField.is_multiple_file) === 1 && target.files.length > 5) {
      this.customField.errorMessage[entityIndex] = 'error_messages.maximum_x_files';
      this.customField.customerSelectedInputValue[entityIndex] = '';
      this.waitForFileUpload[entityIndex] = false;
      this.customFieldsService.imageUploaded = true;
      return;
    }

    if (Number(this.customField.is_multiple_file) === 1 && this.customField.customerSelectedFileValue[entityIndex]?.length) {
      const totalFileLength = target.files.length + this.customField.customerSelectedFileValue[entityIndex].length;
      if (totalFileLength > 5) {
        this.customField.errorMessage[entityIndex] = 'error_messages.maximum_x_files';
        this.customField.customerSelectedInputValue[entityIndex] = '';
        this.waitForFileUpload[entityIndex] = false;
        this.customFieldsService.imageUploaded = true;
        return;
      }
    }

    for (let index = 0; index < target?.files?.length; index++) {
      const file = target.files[index];

      if (this.customField.customSupportedMimeType?.split(',')?.indexOf(file.type) === -1) {
        invalidFileType = true;
        this.customField.errorMessage[entityIndex] = this.customField.supported_mime_type === 'pdf'
          ? 'error_messages.document_invalid_file_type'
          : 'error_messages.image_invalid_file_type';
        this.customField.customerSelectedInputValue[entityIndex] = '';
        this.waitForFileUpload[entityIndex] = false;
        this.customFieldsService.imageUploaded = true;
        break;
      }

      if (!invalidFileType && file.size > Number(this.maxFileSize)) {
        this.customField.errorMessage[entityIndex] = 'error_messages.max_file_size_5mb';
        this.customField.customerSelectedInputValue[entityIndex] = '';
        largeFileSize = true;
        this.waitForFileUpload[entityIndex] = false;
        this.customFieldsService.imageUploaded = true;
        break;
      }
    }

    if (invalidFileType || largeFileSize) { return; }

    this.waitForFileUpload[entityIndex] = true;

    if (this.customerDataIsNotMandatoryToUploadFiles) {
      this.uploadCustomFieldFiles(target, entityIndex);
    } else {
      this.uploadCustomerCustomFieldFiles(target, entityIndex);
    }
  }

  uploadCustomFieldFiles(target: DataTransfer, entityIndex = 0): void {
    this.customFieldsService.uploadCustomFieldFiles(target.files, this.customField).subscribe({
      next: result => {
        this.customFieldsService.imageUploaded = true;
        this.createCustomFieldFileValues(result, entityIndex);
      },
      error: (errorResponse: HttpErrorResponse) => {
        this.customField.customerSelectedInputValue[entityIndex] = '';
        LoggerService.error(errorResponse);
        // setting imageUploaded to true even file is failed to upload reason
        // to let user to submit form when first image attampt is failed and they want to
        // continue without uploading files
        if (this.customField.required === 0) {
          this.customFieldsService.imageUploaded = true;
        }
        this.waitForFileUpload[entityIndex] = false;
        if (errorResponse.error && errorResponse.statusText === 'file-size') {
          this.customField.errorMessage[entityIndex] = 'error_messages.max_file_size_5mb';
        } else if (errorResponse.error && errorResponse.statusText === 'malware') {
          this.customField.errorMessage[entityIndex] = errorResponse.error.message;
        } else {
          this.customField.errorMessage[entityIndex] = 'error_messages.something_went_wrong';
        }
      }
    });
  }

  uploadCustomerCustomFieldFiles(target: DataTransfer, entityIndex = 0): void {
    this.customFieldsService.uploadCustomerCustomFieldFiles(
      target.files,
      this.customField,
      this.surveyService.customerDetail.id as number
    ).subscribe({
      next: customerCustomFields => {
        this.waitForFileUpload[entityIndex] = false;
        this.customFieldsService.imageUploaded = true;

        if (!customerCustomFields) {
          return;
        }
        if (!this.surveyService.customerDetail.custom_fields) {
          this.surveyService.customerDetail.custom_fields = [];
        }

        this.surveyService.customerDetail.custom_fields = this.surveyService.customerDetail.custom_fields.concat(customerCustomFields);
        this.createFileList(entityIndex);
      },
      error: (errorResponse: HttpErrorResponse) => {
        this.customField.customerSelectedInputValue[entityIndex] = '';
        LoggerService.error(errorResponse);
        // setting imageUploaded to true even file is failed to upload reason
        // to let user to submit form when first image attampt is failed and they want to
        // continue without uploading files
        if (this.customField.required === 0) {
          this.customFieldsService.imageUploaded = true;
        }
        this.waitForFileUpload[entityIndex] = false;
        if (errorResponse.error && errorResponse.statusText === 'file-size') {
          this.customField.errorMessage[entityIndex] = 'error_messages.max_file_size_5mb';
        } else if (errorResponse.error && errorResponse.statusText === 'malware') {
          this.customField.errorMessage[entityIndex] = errorResponse.error.message;
        } else {
          this.customField.errorMessage[entityIndex] = 'error_messages.something_went_wrong';
        }
      }
    });
  }

  updateMimeTypes(): void {
    this.customField.customSupportedMimeType = '';
    if (this.customField.supported_mime_type) {
      if (this.customField.supported_mime_type.indexOf(',') === -1) {
        if (this.customField.supported_mime_type === 'image') {
          this.customField.customSupportedMimeType = MIME_TYPES.IMAGE;
        } else if (this.customField.supported_mime_type === 'pdf') {
          this.customField.customSupportedMimeType = MIME_TYPES.DOC;
        }
      } else {
        const types: string[] = (this.customField.supported_mime_type as string).split(',');
        for (const type of types) {
          if (type === 'image') {
            this.customField.customSupportedMimeType = this.customField.customSupportedMimeType + MIME_TYPES.IMAGE + ',';
          } else if (type === 'pdf') {
            this.customField.customSupportedMimeType = this.customField.customSupportedMimeType + MIME_TYPES.DOC + ',';
          }
        }
      }
    }
  }

  toggleFileSelection(
    event: MatCheckboxChange,
    file: {
      file: CustomCustomFieldFileDbModel,
      customerCustomFieldUuids: string, selected: boolean
    }): void {
    if (event.checked) {
      this.selectedFiles.push(file.customerCustomFieldUuids);
    } else {
      this.selectedFiles = this.selectedFiles.filter((item: string) => {
        return item !== file.customerCustomFieldUuids;
      });
    }
  }

  deleteFile(
    file: {
      file: CustomCustomFieldFileDbModel,
      customerCustomFieldUuids: string
    },
    index: number,
    entityIndex = 0
  ): void {
    if (!this.isEditAllowed) {
      return;
    }
    if (file.customerCustomFieldUuids) {
      this.customFieldsService.deleteCustomerCustomFieldFile(file.customerCustomFieldUuids).subscribe({
        next: () => {
          this.surveyService.customerDetail.custom_fields = this.surveyService.customerDetail.custom_fields
            .filter((customerCustomField: CustomerCustomFieldDbModel) => {
              if (customerCustomField.file && customerCustomField.file.id === file.file.id) {
                return false;
              } else {
                return true;
              }
            });
          // LoggerService.log('after this.customer.custom_fields ', this.customer.custom_fields);
          this.customField.customerSelectedFileValue[entityIndex].splice(index, 1);
          this.selectedFiles.splice(this.selectedFiles.indexOf(file.customerCustomFieldUuids), 1);
          this.setCustomerFileSelectedValue(entityIndex);
        },
        error: (error: HttpErrorResponse) => LoggerService.error(error)
      });
    } else {
      this.customFieldsService.deleteFile(file.file.uuid, this.customField.id).subscribe({
        next: (result: { success: boolean }) => {
          LoggerService.log('result ', result);
          this.customField.customerSelectedFileValue[entityIndex].splice(index, 1);
          this.selectedFiles.splice(this.selectedFiles.indexOf(file.customerCustomFieldUuids), 1);
          this.setCustomerFileSelectedValue(entityIndex);
        },
        error: (error: HttpErrorResponse) => {
          LoggerService.error(error);
        }
      });
    }
  }

  setCustomerFileSelectedValue(entityIndex = 0): void {
    if (this.customField.customerSelectedFileValue[entityIndex]?.length > 0) {
      this.customField.customerSelectedValue[entityIndex] = this.customField.customerSelectedFileValue[entityIndex]
        .map(item => item.file.id);
    } else {
      this.customField.customerSelectedValue[entityIndex] = [];
    }
    if (this.customField.customerSelectedValue[entityIndex]?.length === 0) {
      this.customField.customerSelectedInputValue[entityIndex] = [];
    }
    this.waitForFileUpload[entityIndex] = false;
  }

  getFilesByFileIds(fileIds: number[]): void {
    if (!fileIds || fileIds?.length === 0) {
      LoggerService.warn('File ids are missing');
      return;
    }
    this.customFieldsService.getFilesByFileIds(fileIds).subscribe({
      next: (result: CustomCustomFieldFileDbModel[]) => {
        this.createCustomFieldFileValues(result);
      },
      error: (error: HttpErrorResponse) => {
        LoggerService.error(error);
      }
    });
  }

  createCustomFieldFileValues(uploadedFiles: CustomCustomFieldFileDbModel[], entityIndex = 0): void {
    LoggerService.log('uploadedFiles ', uploadedFiles);
    if (!uploadedFiles) {
      this.waitForFileUpload[entityIndex] = false;
      return;
    }

    const finalData: { custom_field_id: number, file_ids: number[], type: string } = {
      custom_field_id: this.customField.id,
      file_ids: [],
      type: CUSTOM_FIELD_TYPES.CUSTOM_FIELD_FILE
    };

    for (const fileResult of (uploadedFiles as CustomCustomFieldFileDbModel[])) {
      finalData.file_ids.push(fileResult.id);
    }

    // this.customerService.setCustomerCustomFieldFilesFormDataInLocalStorage(finalData);

    if (Number(this.customField.is_multiple_file) === 1) {
      if (!this.customField.customerSelectedFileValue[entityIndex]) {
        this.customField.customerSelectedFileValue[entityIndex] = [];
      }
    } else {
      this.customField.customerSelectedFileValue[entityIndex] = [];
    }

    LoggerService.log('this.customField.customerSelectedFileValue ', this.customField.customerSelectedFileValue);

    for (const fileResult of (uploadedFiles as CustomCustomFieldFileDbModel[])) {
      this.customField.customerSelectedFileValue[entityIndex].push({
        file: fileResult,
        customerCustomFieldUuids: undefined, // TODO need to verify what to pass here
        selected: false,
      });
    }
    this.setCustomerFileSelectedValue(entityIndex);
    // this.customField.customerSelectedInputValue = null;

    this.updateMimeTypes();
  }

  openYoutubeVideo(option: CustomFieldOptionModel, youtubeElement: any): void {
    let position: { top: string };
    LoggerService.log('Inside openYoutubeVideo() youtubeElement ', youtubeElement);
    if (this.widgetService.layoutType === 'IFRAME') {
      const youtubeElementRef: ElementRef = new ElementRef(youtubeElement);
      LoggerService.log('youtubeElementRef ', youtubeElementRef);
      let dialogTop = 100;
      if (youtubeElementRef.nativeElement) {
        const temp: any = youtubeElementRef.nativeElement.getBoundingClientRect();
        LoggerService.log('Anchor element tag position is ', temp);
        if (temp?.top) {
          dialogTop = temp.top - 100;
        }
      }
      position = {
        top: `${dialogTop}px`
      };
    }

    const matDialogConfig: MatDialogConfig = {
      data: {
        youtubeUrl: option.youtube_url
      },
      panelClass: 'calenso-generic-dialog-box',
      width: '550px',
    };

    if (this.widgetService.layoutType === 'IFRAME') {
      matDialogConfig.position = position;
    }

    this.matDialog.open(YoutubeModalComponent, matDialogConfig).afterClosed().subscribe(
      (result: { success: boolean }) => {
        // LoggerService.log('final result is ', result);
        if (result && result.success) {

        }
      }
    );
  }

  openImagePopup(option: CustomFieldOptionModel, imageElement: any): void {
    let position: { top: string };
    if (this.widgetService.layoutType === 'IFRAME') {
      const imageElementRef: ElementRef = new ElementRef(imageElement);
      let dialogTop = 100;
      if (imageElementRef.nativeElement) {
        const temp: any = imageElementRef.nativeElement.getBoundingClientRect();
        dialogTop = temp?.top ? (temp.top - 100) : dialogTop;
      }
      position = {
        top: `${dialogTop}px`
      };
    }

    const matDialogConfig: MatDialogConfig = {
      data: {
        imageUrl: option.image
      },
      panelClass: 'calenso-image-popup-dialog-box',
      width: '550px',
    };

    if (imageElement?.naturalHeight && imageElement?.naturalWidth) {
      matDialogConfig.width = `${imageElement?.naturalWidth}px`;
    }

    if (this.widgetService.layoutType === 'IFRAME') {
      matDialogConfig.position = position;
    }

    this.matDialog.open(ImageModalComponent, matDialogConfig).afterClosed().subscribe(
      (result: { success: boolean }) => {
        if (result && result.success) {

        }
      }
    );
  }

  private createFileList(entityIndex = 0): void {
    this.customField.customerSelectedFileValue[entityIndex] = [];
    if (this.customField.type === CUSTOM_FIELD_TYPES.CUSTOM_FIELD_FILE) {
      // LoggerService.log('this.customField ', this.customField);
      for (const customerCustomField of this.surveyService.customerDetail.custom_fields) {
        if (customerCustomField.custom_field_id === this.customField.id) {
          this.customField.customerSelectedFileValue[entityIndex].push({
            file: customerCustomField.file,
            customerCustomFieldUuids: customerCustomField.uuid,
            selected: false,
          });
        }
      }
      this.updateMimeTypes();
      this.setCustomerFileSelectedValue(entityIndex);
    }
  }

  hasOptionWithFilledImage(entityIndex = 0): boolean {
    const hasOption = this.customField.custom_field_options[entityIndex].find(
      (option: CustomFieldOptionModel) => option.is_fullscreen_image === 1
    );
    return hasOption ? true : false;
  }

  preSelectWorkerAndStore(worker: WorkerDbModel): void {
    if (worker) {
      // Every branch below can change the answered store, not just the clearing one: the
      // single-store branches rebind it to the picked worker's own store. Compare the effective
      // scope before and after instead of flagging one branch, so any change notifies. Without
      // it, a pick made while a scoped fetch is still in flight moves the scope with nothing
      // re-entering getWorkers() - the in-flight reply is then dropped by the stale-scope guard
      // and the loading flag is never cleared by anyone.
      const scopeBefore = this.answeredStoreScopeId();
      this.bookingService.selectedBookingData.currentSelectedAppointmentState = this.bookingService.resetCurrentSelectedAppointmentStateonStoreSelection();

      // set pre-selected worker and store flag
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.storeAndWorkerPreSelectedThroughWorkerSelectField = true;
      // Optional: a caller without a mounted question leaves this undefined, and the restriction
      // then fails CLOSED (it returns the option list untouched) rather than filtering against a
      // question id it does not have.
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.workerSelectQuestionId = this.customField?.id;

      // pre-select store — a multi-store worker (worker.stores[]) has no single primary
      // to preselect: keep an already-selected store the worker serves, else preselect
      // the sole served store, else leave it for the store step. Single-store workers
      // (addon off) keep today's behaviour: preselect worker.store_id / worker.store.
      // A scoped row (POST workers/filter) carries no `store` - the endpoint does not contain
      // Stores for a non-addon partner. Fall back to the store already in the appointment state
      // when it is the SAME store, which reset...onStoreSelection() just carried over; otherwise
      // writing worker.store here would blank the store the customer answered.
      const preservedStore = this.bookingService.selectedBookingData
        .currentSelectedAppointmentState.selectedStore;
      const primaryStore = worker.store
        ?? (preservedStore?.id === worker.store_id ? preservedStore : undefined);
      let resolvedStore = primaryStore;
      if (worker.stores?.length) {
        const alreadySelectedStoreId = this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId;
        if (alreadySelectedStoreId != null && workerServesStore(worker, alreadySelectedStoreId)) {
          resolvedStore = worker.stores.find(s => s.id === alreadySelectedStoreId)
            ?? this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStore;
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId = alreadySelectedStoreId;
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStore = resolvedStore;
        } else if (worker.stores.length === 1) {
          resolvedStore = worker.stores[0];
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId = worker.stores[0].id;
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStore = worker.stores[0];
        } else {
          // no single store to preselect — let the store step choose. Clear any previously
          // selected store the new worker does not serve, otherwise it survives into the store
          // step and the slot query runs worker x non-served store.
          resolvedStore = null;
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId = undefined;
          this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStore = undefined;

        }
      } else {
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreId = worker.store_id;
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStore = primaryStore;
      }

      // pre-select worker
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId = worker.id;
      this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorker = worker;

      if (
        resolvedStore && !resolvedStore.is_online && (
          this.widgetService?.widgetConf?.partner?.enable_store_postcode_feature === 1 ||
          this.widgetService?.widgetConf?.partner?.automatic_store_zip_search === 1
        )) {
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedStoreLeadGeneratorPostcode = resolvedStore.zip;
        // A store's own zip, not the customer's postcode: never prefilled.
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.storeLeadGeneratorPostcodeEnteredByCustomer = false;
      }

      this.bookingService.setAppointmentStateInLocalStorage(this.bookingService.selectedBookingData.currentSelectedAppointmentState);

      // Silent when the scope did not move (null -> null with no store question in play, or a
      // same-store pick), so the unscoped path issues no extra request.
      if (this.answeredStoreScopeId() !== scopeBefore) {
        this.bookingService.notifyStoreContextChanged();
      }
    }
  }

  /**
   * Write this question's store into the appointment state. Pure: no reset, no worker-mismatch
   * cleanup, no store-context notification, and it does NOT persist — the caller owns that, so
   * the change path below still performs exactly one write to local storage.
   *
   * Split out of preSelectStore() so the RESTORE path can re-derive the scope from an answer
   * that came back from the draft rather than from a user gesture. Calling preSelectStore()
   * there is not an option, and that is the whole reason this split exists: it resets the
   * appointment state and then DELETES a mismatching worker-select answer from the draft. Both
   * are correct when the CUSTOMER changes the store; on a restore both destroy the very answers
   * the customer pressed Back to return to.
   */
  private applyStoreScope(store: StoreDbModel): void {
    const currentSelectedAppointmentState = this.bookingService.selectedBookingData.currentSelectedAppointmentState;

    // set pre-selected store flag (NOT the worker-select flag — store-only preselection must
    // not trigger the worker-step-skipping / advisor-service-restriction behaviors that flag
    // drives elsewhere)
    currentSelectedAppointmentState.storePreSelectedThroughStoreSelectField = true;

    // pre-select store
    currentSelectedAppointmentState.selectedStoreId = store.id;
    currentSelectedAppointmentState.selectedStore = store;

    if (
      !store.is_online && (
        this.widgetService?.widgetConf?.partner?.enable_store_postcode_feature === 1 ||
        this.widgetService?.widgetConf?.partner?.automatic_store_zip_search === 1
      )) {
      currentSelectedAppointmentState.selectedStoreLeadGeneratorPostcode = store.zip;
      // Also runs on the restore path with no reset, so a carried `true` must not sit next to a store zip.
      currentSelectedAppointmentState.storeLeadGeneratorPostcodeEnteredByCustomer = false;
    }
  }

  /**
   * Re-derive the store scope from an answer this question was RESTORED with — Back navigation,
   * or any other re-mount whose value arrives through prefillCustomFieldValue() / a query param
   * / context.booking_questions_prefill.
   *
   * preSelectStore() runs only from the ng-select change handler, and a restored value fires no
   * change event. Without this the customer sees their store in the dropdown while
   * selectedStoreId is unset, so every store-scoped consumer — the advisor list, dependent
   * questions, the slot query — behaves as if no store had ever been chosen.
   *
   * Runs once the store list has resolved, because the answer is an id that has to be matched
   * against a real store object: the appointment state stores the whole row, not just the id.
   */
  private restoreStoreScopeFromAnswer(): void {
    if (!this.bookingService.selectedBookingData?.currentSelectedAppointmentState) {
      return;
    }

    // ng-select binds an ARRAY for a [multiple] question and a store answer drives exactly one
    // store scope — same unwrap as the change handler, for the same reason.
    const answeredValue = this.customField.customerSelectedValue?.[0];
    const answeredStoreId = Array.isArray(answeredValue) ? answeredValue[0] : answeredValue;
    if (answeredStoreId === null || answeredStoreId === undefined || answeredStoreId === '') {
      return;
    }

    const store = this.stores.find(item => item.id === Number(answeredStoreId));
    if (!store) {
      // The answered store is no longer on offer (deactivated, or filtered out as internal-only).
      // Leave the scope untouched rather than inventing one — clearing it here would be
      // indistinguishable from the customer deliberately clearing the field.
      return;
    }

    const scopeBefore = this.answeredStoreScopeId();
    this.applyStoreScope(store);
    this.bookingService.setAppointmentStateInLocalStorage(
      this.bookingService.selectedBookingData.currentSelectedAppointmentState
    );

    // Only when the effective scope actually moved. Every mounted worker-select question
    // re-fetches its pool on this event, so notifying on an unchanged scope would fire a
    // redundant workers request on each re-mount. Emitting is safe on a restore: the listeners
    // re-filter their lists, they never delete an answer — that is preSelectStore()'s cleanup,
    // which this path deliberately does not run.
    if (this.answeredStoreScopeId() !== scopeBefore) {
      this.bookingService.notifyStoreContextChanged();
    }
  }

  /**
   * Ask the host to re-load the question this field declares through child_custom_field_id.
   *
   * store-select and worker-select declare their dependent question on the FIELD
   * (child_custom_field_id), not on an option, and the host loads it from
   * onRadioButtonChangeEvent(). That event only ever comes from a change handler, so a RESTORED
   * answer leaves the child unloaded: press Back and the advisor question, the topic question and
   * everything under them are simply gone, while the store still shows in its own dropdown.
   *
   * The option-based types already replay this on restore through loadOptionDependentQuestions();
   * these two have no options at all, so that replay cannot reach them and they need their own.
   *
   * The event is deliberately shaped to do exactly one thing. `customFieldOption: null` skips the
   * host's option-dependency branch, and an empty oldCustomFieldDependencyIds with
   * removeOldCustomFieldDependencyIds false skips purgeStaleDependencyAnswers() - a restore must
   * not purge the very answers it is restoring. What is left is the child_custom_field_id branch.
   */
  private restoreChildQuestionFromAnswer(): void {
    if (this.childQuestionRestored || !this.customField.child_custom_field_id) {
      return;
    }

    const answered = Array.isArray(this.customField.customerSelectedValue)
      ? this.customField.customerSelectedValue[0]
      : this.customField.customerSelectedValue;
    if (answered === null || answered === undefined || answered === '') {
      return;
    }

    this.childQuestionRestored = true;
    this.radioButtonChangeEvent.emit({
      customField: this.customField,
      customFieldOption: null,
      oldCustomFieldDependencyIds: [],
      removeOldCustomFieldDependencyIds: false,
    });
  }

  /**
   * Write this question's advisor into the appointment state. Pure, for the same reason
   * applyStoreScope() is: preSelectWorkerAndStore() begins by RESETTING the appointment state and
   * then re-resolves the store from the worker, which on a restore would undo the store scope the
   * store question has just re-derived from its own answer.
   */
  private applyWorkerScope(worker: WorkerDbModel): void {
    const currentSelectedAppointmentState = this.bookingService.selectedBookingData.currentSelectedAppointmentState;

    currentSelectedAppointmentState.storeAndWorkerPreSelectedThroughWorkerSelectField = true;
    currentSelectedAppointmentState.workerSelectQuestionId = this.customField?.id;
    currentSelectedAppointmentState.selectedWorkerId = worker.id;
    currentSelectedAppointmentState.selectedWorker = worker;
  }

  /**
   * Re-derive the advisor from an answer this question was RESTORED with.
   *
   * Only the appointment state - the child question is re-loaded by loadOptionDependentQuestions(),
   * which does not need the answer resolved and so also covers the "anybody" sentinel.
   *
   * The twin of restoreStoreScopeFromAnswer(), and it runs at the same point in the lifecycle:
   * once the pool has resolved, because the appointment state holds the whole worker row while the
   * answer is only an id. It runs AFTER applyStoreScopeToWorkers() so it can only ever resolve an
   * advisor that survived the store prune - an answer that did not survive has already been
   * dropped from the draft and must not be written back into the state here.
   *
   * Returns whether it actually (re)applied the advisor, so the caller can tell a real repair
   * from a no-op. That matters on the cold-pool path below.
   *
   * Called from TWO places on purpose, unlike the store twin. WorkerService.getWorkers() answers
   * from local storage SYNCHRONOUSLY once the cache is warm, so on a re-mount the pool handler can
   * run while customerSelectedValue is still empty - ngOnInit only prefills the restored answer
   * further down. Called from the pool handler alone, the restore would find no answer and give
   * up; called from the end of ngOnInit alone, it would miss the genuinely async first load.
   * Whichever runs first wins and the other is a no-op, because the write below is guarded on the
   * state not already carrying this advisor.
   */
  private restoreWorkerScopeFromAnswer(): boolean {
    const state = this.bookingService.selectedBookingData?.currentSelectedAppointmentState;
    if (!state) {
      return false;
    }

    const answeredValue = this.customField.customerSelectedValue?.[0];
    const answeredWorkerId = Array.isArray(answeredValue) ? answeredValue[0] : answeredValue;
    if (answeredWorkerId === null || answeredWorkerId === undefined || answeredWorkerId === '') {
      return false;
    }

    const worker = this.workers.find(item => item.id === Number(answeredWorkerId));
    if (!worker) {
      // The answered advisor is no longer offered. applyStoreScopeToWorkers() owns that case and
      // has already narrowed or dropped the draft entry; leave the state alone rather than writing
      // back an advisor the customer can no longer pick.
      return false;
    }

    // The row in the state is not necessarily the row this question chose. Once the customer has
    // passed the workers step, selectedWorker has been REPLACED by a service-derived row, and the
    // endpoints behind that step (appointment_services_by_meeting_type / _by_store) do not contain
    // ServicesWorkers - so the replacement carries no services_workers. Re-deriving only on an id
    // or flag mismatch would leave that stripped row in place, and the advisor-scoped option
    // restriction, which keys on services_workers, would silently switch itself off on exactly the
    // Back navigation this restore exists to support. Refresh the row when ours can repair it.
    const storedWorker = state.selectedWorker;
    const storedRowLostItsServiceMap = !storedWorker?.services_workers?.length &&
      !!worker.services_workers?.length;

    if (
      state.selectedWorkerId !== worker.id ||
      !state.storeAndWorkerPreSelectedThroughWorkerSelectField ||
      storedRowLostItsServiceMap
    ) {
      this.applyWorkerScope(worker);
      this.bookingService.setAppointmentStateInLocalStorage(
        this.bookingService.selectedBookingData.currentSelectedAppointmentState
      );
      return true;
    }

    return false;
  }

  preSelectStore(store: StoreDbModel): void {
    if (store) {
      const previousState = this.bookingService.selectedBookingData.currentSelectedAppointmentState;
      this.bookingService.selectedBookingData.currentSelectedAppointmentState = this.bookingService.resetCurrentSelectedAppointmentStateonStoreSelection();

      this.applyStoreScope(store);

      // Worker-mismatch cleanup: if a worker was already answered (question ordering: worker
      // question answered first, store changed afterwards) and that worker does not belong to
      // the newly-selected store, clear it — otherwise a stale advisor from the old store would
      // ride through as both appointment state AND a stale worker-select custom-field answer.
      // workerServesStore() rather than a raw store_id compare, for the same multi-store reason
      // as the worker-list filter in getWorkers(): a scalar mismatch would both wipe valid
      // answers (worker serves this store via stores[], just not as its primary) and miss real
      // mismatches. preSelectWorkerAndStore() above already handles stores[] the same way.
      const previouslySelectedWorker = previousState?.selectedWorker;
      if (previouslySelectedWorker && !workerServesStore(previouslySelectedWorker, store.id)) {
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorkerId = null;
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.selectedWorker = null;
        this.bookingService.selectedBookingData.currentSelectedAppointmentState.storeAndWorkerPreSelectedThroughWorkerSelectField = false;

        this.bookingService.selectedBookingData.selectedCustomFieldValues = (this.bookingService.selectedBookingData.selectedCustomFieldValues || [])
          .filter(value => value.type !== this.customFieldConst.CUSTOM_FIELD_WORKER_SELECT);
        this.bookingService.setCustomFieldValuesInLocalStorage(this.bookingService.selectedBookingData.selectedCustomFieldValues, false);

        // A (possibly required) worker-select answer was just silently dropped — let an ancestor
        // re-surface that question instead of the user finding out only at submit time.
        this.workerSelectAnswerInvalidated.emit();
      }

      this.bookingService.setAppointmentStateInLocalStorage(this.bookingService.selectedBookingData.currentSelectedAppointmentState);
      // Tell every mounted worker-select question to re-apply the store scope. Where the worker
      // question mounts strictly AFTER this answer (two top-level questions on the stepped view)
      // this is redundant — its own ngOnInit already filtered. Wherever the two are co-mounted
      // (personal form, a nested dependency chain, one-page cross-step stacking) it is the only
      // thing that filters the advisor list at all.
      // Scope note: this makes a mismatching worker unpickable through the worker-select
      // QUESTION. It does not cover the dedicated workers step — workers-selection.component.ts
      // filters by context.store_uuids instead of the selected store when that config is set,
      // which is pre-existing and tracked separately.
      this.bookingService.notifyStoreContextChanged();
    }
  }

  /**
   * Undo exactly what preSelectWorkerAndStore() set, when the worker-select answer no longer names
   * an advisor. The twin of clearPreSelectedStore() below, and guarded the same way: the flag can
   * only have been set by THIS field's own preSelectWorkerAndStore(), so it is what proves we are
   * undoing our own prior write and not a store-select question's.
   *
   * The store is deliberately left alone - it is a different question's answer, and dropping the
   * advisor is not a reason to drop the branch the customer chose.
   */
  private clearPreSelectedWorker(): void {
    const currentSelectedAppointmentState = this.bookingService.selectedBookingData.currentSelectedAppointmentState;
    if (!currentSelectedAppointmentState?.storeAndWorkerPreSelectedThroughWorkerSelectField) {
      return;
    }

    currentSelectedAppointmentState.storeAndWorkerPreSelectedThroughWorkerSelectField = false;
    currentSelectedAppointmentState.workerSelectQuestionId = undefined;
    currentSelectedAppointmentState.selectedWorkerId = null;
    currentSelectedAppointmentState.selectedWorker = null;

    this.bookingService.setAppointmentStateInLocalStorage(currentSelectedAppointmentState);
  }

  /**
   * Undo exactly what preSelectStore() itself set, when the user clears the store-select
   * ng-select. Only acts when storePreSelectedThroughStoreSelectField is true — that flag can
   * only have been set by THIS field's own preSelectStore(), so it's the guard that we're
   * undoing our own prior write and never someone else's (e.g. worker-select's store flag).
   */
  private clearPreSelectedStore(): void {
    const currentSelectedAppointmentState = this.bookingService.selectedBookingData.currentSelectedAppointmentState;
    if (!currentSelectedAppointmentState?.storePreSelectedThroughStoreSelectField) {
      return;
    }

    currentSelectedAppointmentState.storePreSelectedThroughStoreSelectField = false;
    currentSelectedAppointmentState.selectedStoreId = null;
    currentSelectedAppointmentState.selectedStore = null;
    if (currentSelectedAppointmentState.selectedStoreLeadGeneratorPostcode) {
      currentSelectedAppointmentState.selectedStoreLeadGeneratorPostcode = null;
      currentSelectedAppointmentState.storeLeadGeneratorPostcodeEnteredByCustomer = false;
    }

    this.bookingService.setAppointmentStateInLocalStorage(currentSelectedAppointmentState);
    this.bookingService.notifyStoreContextChanged();
  }
}

